import './styles.css';

import React, { useCallback, useMemo } from 'react';
import { Header as LayoutComponentHeader } from '@vtblife/layout-components';
import { useShellContext } from '@vtblife/layout-components';

import { OfferRoute, SearchMapRoute, SearchRoute, UnsubscribeRoute, useRouter } from '@search/offer-search-routes/src';
import { errorCatcherWrap } from '@search/error/src/catcher';
import { useM2AuthContext } from '@search/auth/src/M2AuthProvider';
import classname from '@search/classname/src';
import { getDeveloperPromoName } from '@search/vtbeco-frontend-core/domain/newbuilding/developer-promo/helpers';
import { dataLayerPush } from '@search/vtbeco-frontend-core/domain/google/dataLayerPush';
import { useGeoRootRegion } from '@search/geo/src/GeoRootRegionProvider';
import { RegionIdEnum } from '@search/vtbeco-frontend-core/enums';
import { AdditionalRegionWithMetroIdEnum, AdditionalRegionWithMetroIdEnumObl } from '@search/filter-enums/enums/Region';
import { getDeveloperBannerGtmData } from '@search/vtbeco-frontend-core/domain/newbuilding/developer-promo/analytics';
import { DeveloperWithPromoName } from '@search/vtbeco-frontend-core/domain/newbuilding/developer-promo/types';
import { ClShellConfigContext, useCustomConfig } from '../ClShellProvider';

// eslint-disable-next-line max-len
const POSSIBLE_ACTIVE_PARTNERS_SET = new Set([ 'samolet' ]);

const cn = classname.bind(null, 'ClHeader');

export function getRegionForPromo(regionId: number, narrowRegionId?: number) {
    if (
        ! narrowRegionId ||
        Object.values(RegionIdEnum).includes(regionId) ||
        regionId === AdditionalRegionWithMetroIdEnum.EKB ||
        regionId === AdditionalRegionWithMetroIdEnumObl.SVERDLOVSKAYA_OBLAST
    ) {
        return regionId;
    }

    return narrowRegionId;
}

const ClHeader: React.FC<{
    buildLink?: (url: string) => string;
    regionId?: number;
    isFullWidth?: boolean;
    isBordered?: boolean;
}> = ({
    buildLink,
    regionId,
    isFullWidth = true,
    isBordered,
}) => {
    const { auth } = useM2AuthContext();
    const { config } = useShellContext() as unknown as { config: ClShellConfigContext };
    const router = useRouter();

    // Костыль, не работает useShellContext
    const shellConfig = useCustomConfig();

    const rootRegions = useGeoRootRegion();
    const narrowRegionId = regionId && rootRegions.byId(regionId)?.narrowRegion?.id;
    const regionForDeveloperPromo = regionId && getRegionForPromo(regionId, narrowRegionId);

    const developerWithPromoNameByRegion = useMemo<DeveloperWithPromoName | undefined>(() => {
        const resultPartner = getDeveloperPromoName(regionForDeveloperPromo);

        // @ts-expect-error
        return POSSIBLE_ACTIVE_PARTNERS_SET.has(resultPartner) ? resultPartner : undefined;
    }, [ regionForDeveloperPromo ]);
    const partners = useMemo(() => {
        // eslint-disable-next-line max-len
        return Object.fromEntries(Object.entries((config ?? shellConfig)?.header?.partners ?? {}).filter(([ key, value ]) => POSSIBLE_ACTIVE_PARTNERS_SET.has(key) && value));
    }, [ config?.header?.partners ]);

    const pageType = useMemo(() => {
        const { route } = router.resolve([ SearchRoute, OfferRoute, SearchMapRoute, UnsubscribeRoute ]);

        switch (route.displayName) {
            case 'OfferRoute':
                return 'card';
            case 'SearchRoute':
            case 'SearchMapRoute':
            case 'UnsubscribeRoute':
                return 'serp';
            default:
                return;
        }
    }, [ router ]);

    const onPartnerClick = useCallback((partnerName: string) => {
        const dataObj = pageType && getDeveloperBannerGtmData(partnerName, {
            action: 'click',
            section: 'logo',
            pageType
        });

        dataObj && dataLayerPush(dataObj);
    }, [ pageType ]);

    const currentRole = auth.user?.currentRoleType;

    const getUrl = (url?: string | null) => buildLink && url?.startsWith('/') ? buildLink(url) : url;

    const items = (config ?? shellConfig)?.header?.items?.map(item => ({
        ...item,

        path: getUrl(item?.path),

        dropdownMenu: item.dropdownMenu?.map(dropDownItem => ({
            ...dropDownItem,
            url: getUrl(dropDownItem?.url),
            id: dropDownItem.id + (dropDownItem.url ?? '0'),
            dropdownMenu: dropDownItem.dropdownMenu?.map(subDropdownItem => ({
                ...subDropdownItem,
                url: getUrl(subDropdownItem?.url),
                id: subDropdownItem.id + (subDropdownItem.url ?? '0')
            }))
        }))
    })) ?? [];

    return (
        <div className={cn()}>
            <LayoutComponentHeader
                isColored
                isBordered={isBordered}
                isFullWidth={isFullWidth}
                cdnUrl='https://cdn.m2.ru'
                loginPaths={(config ?? shellConfig)?.header?.loginPaths ?? {}}
                items={items}
                partners={partners}
                pathname={router.location.pathname}
                activePartner={developerWithPromoNameByRegion}
                authorizedItems={(config ?? shellConfig)?.sidebar}
                roleType={currentRole}
                logoutPath={`/logout?retpath=${encodeURIComponent(router.currentUrl)}`}
                onPartnerClick={onPartnerClick}
                personalButtons={(config ?? shellConfig)?.header?.personalButtons}
            />
        </div>
    );
};

export default errorCatcherWrap(ClHeader);
